import React, { useCallback, useMemo } from 'react';
import { View, ScrollView, Platform, Text } from 'react-native';
import DefaultScreen from '../../../components/Global/DefaultScreenView';
import apis from '@api/api';
import { useQuery } from '@tanstack/react-query';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import { Spinner, SizableText, YStack, XStack, Card } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useAlertDialog } from '@components/Global/AlertDialog/useAlertDialog';
import globalStyles from '@stylesheet';
import moment from 'moment';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';
import { MaterialIcons } from '@expo/vector-icons';

interface GenericDataItemScreenProps {
  route: {
    params?: {
      dataId?: string;
      contentId?: string;
    };
  };
  navigation: any;
}

const GenericDataItemScreen = ({ route, navigation }: GenericDataItemScreenProps) => {
  const { dataId, contentId } = route?.params || {};
  const theme = themeSelector();
  const { showAlert } = useAlertDialog();

  // Fetch data item
  const { data: itemData, isLoading, error, refetch } = useQuery({
    queryKey: ['genericDataItem', dataId, contentId],
    queryFn: async () => {
      if (!dataId || !contentId) {
        throw new Error('Missing dataId or contentId');
      }

      const response = await apis.getDataContentById(dataId, contentId);

      if (!response) {
        throw new Error('No data returned from API');
      }

      // Extract schema and data
      const schema = response.schema || response.data?.dataId?.dataSchema || response.dataId?.dataSchema || {};
      const data = response.data || response;

      // Normalize data structure - handle both nested (data.data.field) and flat (data.field) structures
      let normalizedData = data;
      if (data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
        // If data has a nested data property, use it
        normalizedData = { ...data, ...data.data };
      }

      return {
        schema: Array.isArray(schema) ? schema : (schema.fields || []),
        data: normalizedData,
        rawResponse: response,
        // Extract metadata
        creator: response.data?.posted_by || response.posted_by || null,
        createdAt: response.data?.createdAt || response.createdAt || null,
        updatedAt: response.data?.updatedAt || response.updatedAt || null,
        enrichmentStatus: response.data?.enrichmentStatus || response.enrichmentStatus || null,
        enrichmentMetadata: response.data?.enrichmentMetadata || response.enrichmentMetadata || null,
        aiNotes: response.data?.aiNotes || response.aiNotes || null,
      };
    },
    enabled: !!dataId && !!contentId,
  });

  // Handle form submission
  // GenerateSchemaForm's submitFunction receives (values, id) where id is the dataId
  const handleSubmit = useCallback(async (values: any, id?: string) => {
    if (!contentId) {
      showAlert('Error', 'Content ID is missing');
      return;
    }

    try {
      await apis.updateDataItem(contentId, values);
      await refetch();
      showAlert('Success', 'Data item updated successfully');
    } catch (error) {
      console.error('Error updating data item:', error);
      showAlert('Error', 'Failed to update data item. Please try again.');
      throw error; // Re-throw so form can handle it
    }
  }, [contentId, refetch, showAlert]);

  // Prepare initial data for form
  const initialFormData = useMemo(() => {
    if (!itemData?.data) return {};

    const formData: any = {};
    const data = itemData.data;

    // Extract all fields from data (handle both nested and flat structures)
    Object.keys(data).forEach(key => {
      // Skip internal fields
      if (key === '_id' || key === 'createdAt' || key === 'updatedAt' || key === 'dataId' || key === 'notes') {
        return;
      }
      formData[key] = data[key];
    });

    return formData;
  }, [itemData?.data]);

  // Format creator information
  const creatorInfo = useMemo(() => {
    if (!itemData?.creator) return null;

    const creator = itemData.creator;
    const profile = creator.profile_information || creator.profileInformation || {};
    const name = profile.name || creator.name || '';
    const surname = profile.surname || creator.surname || '';
    const email = creator.email || '';
    const avatar = profile.avatar || creator.avatar || null;
    const fullName = `${name} ${surname}`.trim() || email || 'Unknown User';

    return {
      name: fullName,
      email,
      avatar
    };
  }, [itemData?.creator]);

  // Format model/AI information
  const modelInfo = useMemo(() => {
    const enrichmentStatus = itemData?.enrichmentStatus;
    const enrichmentMetadata = itemData?.enrichmentMetadata;
    const aiNotes = itemData?.aiNotes;

    if (!enrichmentStatus && !enrichmentMetadata && !aiNotes) {
      return null;
    }

    let modelName = 'Unknown Model';
    let modelDetails = '';

    if (enrichmentMetadata) {
      if (enrichmentMetadata.model) {
        modelName = enrichmentMetadata.model;
      }
      if (enrichmentMetadata.provider) {
        modelDetails = enrichmentMetadata.provider;
      }
    }

    if (enrichmentStatus) {
      modelDetails = enrichmentStatus === 'completed' ? 'Enrichment Completed' :
        enrichmentStatus === 'processing' ? 'Processing' :
          enrichmentStatus === 'failed' ? 'Enrichment Failed' : '';
    }

    return {
      name: modelName,
      details: modelDetails,
      status: enrichmentStatus,
      hasNotes: !!aiNotes
    };
  }, [itemData?.enrichmentStatus, itemData?.enrichmentMetadata, itemData?.aiNotes]);

  if (isLoading) {
    return (
      <DefaultScreen screenTitle="Loading..." noMargin>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Spinner size="large" />
        </View>
      </DefaultScreen>
    );
  }

  if (error) {
    return (
      <DefaultScreen screenTitle="Error" noMargin>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <SizableText color={theme ? '#ef4444' : '#f87171'} textAlign="center">
            Failed to load data item. Please try again.
          </SizableText>
        </View>
      </DefaultScreen>
    );
  }

  if (!itemData || !itemData.schema || itemData.schema.length === 0) {
    return (
      <DefaultScreen screenTitle="Data Item" noMargin>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <SizableText color={theme ? '#64748b' : '#94a3b8'} textAlign="center">
            No schema found for this data item.
          </SizableText>
        </View>
      </DefaultScreen>
    );
  }

  return (
    <DefaultScreen
      screenTitle="Edit Data Item"
      noMargin
      backButton
    >
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          padding: Platform.select({ web: 20, default: 15 }),
          paddingBottom: 50,
          gap: 15
        }}
      >
        {/* Creator and Model Details Card */}
        {(creatorInfo || modelInfo || itemData?.createdAt) && (
          <Card
            backgroundColor={theme ? '#ffffff' : '#1e293b'}
            padding="$4"
            borderRadius={12}
            borderWidth={1}
            borderColor={theme ? '#e2e8f0' : '#334155'}
            shadowColor="#000"
            shadowOffset={{ width: 0, height: 2 }}
            shadowOpacity={0.1}
            shadowRadius={4}
            elevation={3}
          >
            <XStack gap="$4" flexWrap="wrap" alignItems="center">
              {/* Creator Section */}
              {creatorInfo && (
                <XStack
                  flex={1}
                  minWidth={Platform.OS === 'web' ? 200 : undefined}
                  style={Platform.OS !== 'web' ? { minWidth: '45%' } : undefined}
                  gap="$3"
                  alignItems="center"
                  paddingRight="$3"
                  borderRightWidth={modelInfo ? 1 : 0}
                  borderRightColor={theme ? '#e2e8f0' : '#334155'}
                >
                  <View style={{ position: 'relative' }}>
                    {creatorInfo.avatar ? (
                      <AvatarHandler
                        avatar={creatorInfo.avatar}
                        avatarImageStyle={{
                          width: 40,
                          height: 40,
                          borderRadius: 20
                        }}
                      />
                    ) : (
                      <View
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 20,
                          backgroundColor: theme ? '#3b82f6' : '#1e40af',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Text style={{
                          color: 'white',
                          fontSize: 16,
                          fontWeight: 'bold'
                        }}>
                          {creatorInfo.name.charAt(0).toUpperCase()}
                        </Text>
                      </View>
                    )}
                  </View>
                  <YStack flex={1} gap="$1">
                    <SizableText
                      size="$3"
                      fontWeight="600"
                      color={theme ? '#1e293b' : '#f8fafc'}
                    >
                      Creator
                    </SizableText>
                    <SizableText
                      size="$2"
                      color={theme ? '#64748b' : '#94a3b8'}
                      numberOfLines={1}
                    >
                      {creatorInfo.name}
                    </SizableText>
                    {itemData?.createdAt && (
                      <SizableText
                        size="$1"
                        color={theme ? '#94a3b8' : '#64748b'}
                      >
                        {moment(itemData.createdAt).format('MMM DD, YYYY')}
                      </SizableText>
                    )}
                  </YStack>
                </XStack>
              )}

              {/* Model/AI Section */}
              {modelInfo && (
                <XStack
                  flex={1}
                  minWidth={Platform.OS === 'web' ? 200 : undefined}
                  style={Platform.OS !== 'web' ? { minWidth: '45%' } : undefined}
                  gap="$3"
                  alignItems="center"
                  paddingLeft={creatorInfo ? "$3" : 0}
                >
                  <View
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 20,
                      backgroundColor: theme ? '#10b981' : '#059669',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <MaterialIcons
                      name="smart-toy"
                      size={20}
                      color="white"
                    />
                  </View>
                  <YStack flex={1} gap="$1">
                    <SizableText
                      size="$3"
                      fontWeight="600"
                      color={theme ? '#1e293b' : '#f8fafc'}
                    >
                      Model
                    </SizableText>
                    <SizableText
                      size="$2"
                      color={theme ? '#64748b' : '#94a3b8'}
                      numberOfLines={1}
                    >
                      {modelInfo.name}
                    </SizableText>
                    {modelInfo.details && (
                      <SizableText
                        size="$1"
                        color={theme ? '#94a3b8' : '#64748b'}
                      >
                        {modelInfo.details}
                      </SizableText>
                    )}
                  </YStack>
                </XStack>
              )}

              {/* Fallback: Show creation date if no creator/model info */}
              {!creatorInfo && !modelInfo && itemData?.createdAt && (
                <XStack gap="$2" alignItems="center">
                  <MaterialIcons
                    name="calendar-today"
                    size={18}
                    color={theme ? '#64748b' : '#94a3b8'}
                  />
                  <SizableText
                    size="$2"
                    color={theme ? '#64748b' : '#94a3b8'}
                  >
                    Created {moment(itemData.createdAt).format('MMM DD, YYYY')}
                  </SizableText>
                </XStack>
              )}
            </XStack>
          </Card>
        )}

        {/* Schema Form Card */}
        <YStack style={[globalStyles.modernCard]} gap="$4">
          <GenerateSchemaForm
            other={dataId}
            schema={itemData.schema}
            initialData={initialFormData}
            submitFunction={handleSubmit}
            hideButton={false}
            buttonLabel='Update'
            scrollEnabled={false}
          />
        </YStack>
      </ScrollView>
    </DefaultScreen>
  );
};

export default GenericDataItemScreen;

